import React, { useEffect, useRef, useState } from "react";
import { API_URL } from "../../config";
import "./CustomersAndSites.css";

const customerFields = [
  ["name", "Kundenname", 255],
  ["email", "E-Mail", 255],
  ["phone", "Telefon", 50],
  ["address", "Adresse", 500],
];

const siteFields = [
  ["name", "Baustellenname", 255],
  ["address", "Adresse", 500],
  ["notes", "Hinweise", 1000],
];

const CustomersAndSites = () => {
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");
  const [revision, setRevision] = useState(0);
  const busyRef = useRef(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");

    const get = async (path) => {
      const response = await fetch(`${API_URL}/api/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || !Array.isArray(data)) {
        throw new Error(data?.error || "Daten konnten nicht geladen werden.");
      }
      return data;
    };

    const load = async () => {
      try {
        const [customerRows, siteRows] = await Promise.all([
          get("customers"),
          get("work-sites"),
        ]);
        if (!controller.signal.aborted) {
          setCustomers(customerRows);
          setSites(siteRows);
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [token, revision]);

  const activeCustomers = customers.filter(
    (customer) => Number(customer.is_active) === 1
  );

  const openEditor = (kind, item = null) => {
    if (busyRef.current) return;
    setEditor({ kind, id: item?.id ?? null });
    setFormError("");
    setSuccess("");

    setForm(
      kind === "customer"
        ? {
            name: item?.name || "",
            email: item?.email || "",
            phone: item?.phone || "",
            address: item?.address || "",
            is_active: item ? Number(item.is_active) === 1 : true,
          }
        : {
            customer_id: item
              ? String(item.customer_id)
              : String(activeCustomers[0]?.id || ""),
            name: item?.name || "",
            address: item?.address || "",
            notes: item?.notes || "",
            is_active: item ? Number(item.is_active) === 1 : true,
          }
    );
  };

  const closeEditor = () => {
    if (busyRef.current) return;
    setEditor(null);
    setFormError("");
  };

  const change = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (busyRef.current || !editor) return;

    const fields =
      editor.kind === "customer" ? customerFields : siteFields;
    const body = { is_active: form.is_active };

    for (const [name] of fields) {
      body[name] = (form[name] || "").trim();
    }

    if (!body.name) {
      setFormError("Bitte einen Namen angeben.");
      return;
    }

    if (editor.kind === "site" && !editor.id) {
      const customerId = Number(form.customer_id);
      if (
        !Number.isSafeInteger(customerId) ||
        !activeCustomers.some((item) => item.id === customerId)
      ) {
        setFormError("Bitte einen aktiven Kunden auswählen.");
        return;
      }
      body.customer_id = customerId;
    }

    busyRef.current = true;
    setSaving(true);
    setFormError("");
    setSuccess("");

    try {
      const path =
        editor.kind === "customer" ? "customers" : "work-sites";
      const response = await fetch(
        `${API_URL}/api/${path}${editor.id ? "/" + editor.id : ""}`,
        {
          method: editor.id ? "PATCH" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      const data = await response.json().catch(() => null);

      if (!response.ok || data?.success !== true) {
        throw new Error(data?.error || "Speichern wurde nicht bestätigt.");
      }

      setSuccess(
        editor.kind === "customer"
          ? "Kunde wurde gespeichert."
          : "Baustelle wurde gespeichert."
      );
      setEditor(null);
      setLoading(true);
      setRevision((value) => value + 1);
    } catch (err) {
      setFormError(
        err instanceof TypeError
          ? "Keine Serverantwort erhalten. Bitte vor einem erneuten Versuch die Liste aktualisieren und prüfen, ob bereits gespeichert wurde."
          : err.message
      );
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  return (
    <section className="customer-site-management">
      <div className="cs-heading">
        <div>
          <h2>Kunden & Baustellen</h2>
          <p>Kunden und Einsatzorte für die Arbeitsplanung verwalten.</p>
        </div>
        <button
          type="button"
          disabled={loading || saving}
          onClick={() => {
            setLoading(true);
            setRevision((value) => value + 1);
          }}
        >
          Aktualisieren
        </button>
      </div>

      {success && <p className="cs-success" role="status">{success}</p>}
      {error && <p className="cs-error" role="alert">{error}</p>}

      {editor && (
        <form className="cs-editor" onSubmit={save}>
          <h3>
            {editor.kind === "customer" ? "Kunde" : "Baustelle"}
            {editor.id ? " bearbeiten" : " anlegen"}
          </h3>

          <fieldset disabled={saving}>
            {editor.kind === "site" && (
              <label>
                Kunde *
                {editor.id ? (
                  <input
                    value={
                      customers.find(
                        (item) => item.id === Number(form.customer_id)
                      )?.name || "Zugeordneter Kunde"
                    }
                    readOnly
                  />
                ) : (
                  <select
                    name="customer_id"
                    value={form.customer_id}
                    onChange={change}
                    required
                  >
                    <option value="">Kunde auswählen</option>
                    {activeCustomers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            )}

            {(editor.kind === "customer"
              ? customerFields
              : siteFields
            ).map(([name, label, maximum]) => (
              <label key={name}>
                {label}{name === "name" ? " *" : ""}
                {["address", "notes"].includes(name) ? (
                  <textarea
                    name={name}
                    value={form[name]}
                    onChange={change}
                    maxLength={maximum}
                    rows={3}
                  />
                ) : (
                  <input
                    name={name}
                    type={
                      name === "email"
                        ? "email"
                        : name === "phone"
                          ? "tel"
                          : "text"
                    }
                    value={form[name]}
                    onChange={change}
                    maxLength={maximum}
                    required={name === "name"}
                  />
                )}
              </label>
            ))}

            <label className="cs-checkbox">
              <input
                name="is_active"
                type="checkbox"
                checked={form.is_active}
                onChange={change}
              />
              Aktiv
            </label>

            {formError && (
              <p className="cs-error" role="alert">{formError}</p>
            )}

            <div className="cs-actions">
              <button type="submit">
                {saving ? "Wird gespeichert …" : "Speichern"}
              </button>
              <button type="button" onClick={closeEditor}>
                Abbrechen
              </button>
            </div>
          </fieldset>
        </form>
      )}

      {loading ? (
        <p>Daten werden geladen …</p>
      ) : error ? null : (
        <div className="cs-columns">
          <section className="cs-panel">
            <div className="cs-heading">
              <h3>Kunden ({customers.length})</h3>
              <button
                type="button"
                disabled={saving}
                onClick={() => openEditor("customer")}
              >
                Kunde anlegen
              </button>
            </div>

            {!customers.length && <p>Noch keine Kunden vorhanden.</p>}

            {customers.map((customer) => (
              <article className="cs-card" key={customer.id}>
                <strong>{customer.name}</strong>
                <span className="cs-state">
                  {Number(customer.is_active) === 1 ? "Aktiv" : "Inaktiv"}
                </span>
                {customer.email && <p>{customer.email}</p>}
                {customer.phone && <p>{customer.phone}</p>}
                {customer.address && <p>{customer.address}</p>}
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => openEditor("customer", customer)}
                >
                  Bearbeiten
                </button>
              </article>
            ))}
          </section>

          <section className="cs-panel">
            <div className="cs-heading">
              <h3>Baustellen ({sites.length})</h3>
              <button
                type="button"
                disabled={saving || !activeCustomers.length}
                onClick={() => openEditor("site")}
              >
                Baustelle anlegen
              </button>
            </div>

            {!activeCustomers.length && (
              <p>Lege zuerst einen aktiven Kunden an.</p>
            )}
            {!sites.length && <p>Noch keine Baustellen vorhanden.</p>}

            {sites.map((site) => (
              <article className="cs-card" key={site.id}>
                <strong>{site.name}</strong>
                <p>Kunde: {site.customer_name}</p>
                <span className="cs-state">
                  {Number(site.is_active) === 1 ? "Aktiv" : "Inaktiv"}
                  {Number(site.customer_is_active) !== 1
                    ? " · Kunde inaktiv"
                    : ""}
                </span>
                {site.address && <p>{site.address}</p>}
                {site.notes && <p>{site.notes}</p>}
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => openEditor("site", site)}
                >
                  Bearbeiten
                </button>
              </article>
            ))}
          </section>
        </div>
      )}
    </section>
  );
};

export default CustomersAndSites;